import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiDollarSign, FiSearch, FiCheckCircle, FiPlus,
  FiExternalLink, FiList, FiX, FiClock, FiAlertCircle, FiDownload, FiFileText, FiEdit2,
} from 'react-icons/fi';
import API from '../utils/api';
import toast from 'react-hot-toast';
import { useStaffLog } from '../utils/useStaffLog';
import { useAuth } from '../context/AuthContext';
import EditReceiptModal from '../components/common/EditReceiptModal';
import { generatePaymentsPDF } from '../utils/generatePaymentsPDF';
import './SalesOrders.css';

/* ────────────────────────────────────────────────────────────────────
   Helpers
   ──────────────────────────────────────────────────────────────────── */

const money = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmt = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const getCreditInfo = (order) => {
  if (order.paymentType !== 'credit') return null;
  const entries = Array.isArray(order.paymentEntries) ? order.paymentEntries : [];
  const creditAmount = Number(order.grandTotal) || 0;
  const receivedEntries = entries.filter(e => e.type !== 'credit');
  const totalFromEntries = receivedEntries.reduce((s, e) => s + Number(e.amount || 0), 0);
  const effectiveReceived = Math.max(totalFromEntries, Number(order.amountReceived) || 0);
  const balanceLeft = Math.max(0, creditAmount - effectiveReceived);
  const creditStatus = balanceLeft <= 0 ? 'settled' : 'pending';
  const isOverdue = creditStatus === 'pending' && order.dueDate && new Date(order.dueDate) < new Date();
  return { creditAmount, totalReceived: effectiveReceived, balanceLeft, creditStatus, isOverdue };
};

const getPaymentStatus = (order) => {
  if (order.paymentType === 'credit') {
    const ci = getCreditInfo(order);
    if (!ci) return 'unpaid';
    if (ci.creditStatus === 'settled') return 'paid';
    if (ci.isOverdue) return 'overdue';
    return 'credit';
  }
  const grand    = Number(order.grandTotal) || 0;
  const received = Number(order.amountReceived) || 0;
  if (received >= grand) return 'paid';
  if (received > 0)      return 'partial';
  return 'unpaid';
};

// Per-row amounts — single source for table rows AND footer totals
const getAmounts = (order) => {
  const ci       = getCreditInfo(order);
  const grand    = Number(order.grandTotal) || 0;
  const received = ci ? ci.totalReceived : Number(order.amountReceived) || 0;
  const balance  = ci ? ci.balanceLeft  : Math.max(0, grand - received);
  return { grand, received, balance };
};

const STATUS_LABELS = {
  action: 'Needs Action', credit: 'Credit', overdue: 'Overdue', paid: 'Paid',
};

const STATUS_META = {
  paid:    { label: 'Paid',    color: '#16a34a', bg: '#dcfce7' },
  partial: { label: 'Partial', color: '#d97706', bg: '#fef3c7' },
  unpaid:  { label: 'Unpaid',  color: '#dc2626', bg: '#fee2e2' },
  credit:  { label: 'Credit',  color: '#7c3aed', bg: '#ede9fe' },
  overdue: { label: 'Overdue', color: '#b91c1c', bg: '#fee2e2' },
};

const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 };
const inputStyle = {
  width: '100%', boxSizing: 'border-box', padding: '8px 10px',
  border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, outline: 'none',
};

/* ────────────────────────────────────────────────────────────────────
   Credit Block
   ──────────────────────────────────────────────────────────────────── */

const CreditBlock = ({ order }) => {
  const ci = getCreditInfo(order);
  if (!ci) return null;
  const statusColor = ci.creditStatus === 'settled' ? '#16a34a' : ci.isOverdue ? '#b91c1c' : '#7c3aed';
  const statusBg    = ci.creditStatus === 'settled' ? '#dcfce7' : ci.isOverdue ? '#fee2e2' : '#ede9fe';
  const statusLabel = ci.creditStatus === 'settled' ? 'Settled' : ci.isOverdue ? 'Overdue' : 'Pending';

  return (
    <div style={{ background: '#fafafa', border: '1px solid #e5e7eb', borderRadius: 10, padding: '14px 16px', marginBottom: 16 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
        Credit Details
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(80px, 1fr))', gap: 10 }}>
        {[
          { label: 'Credit Amount', value: money(ci.creditAmount),   color: '#111' },
          { label: 'Given Date',    value: fmt(order.orderDate),     color: '#374151' },
          { label: 'Due Date',      value: fmt(order.dueDate),       color: ci.isOverdue ? '#b91c1c' : '#374151' },
          { label: 'Received',      value: money(ci.totalReceived),  color: '#16a34a' },
          { label: 'Balance Left',  value: money(ci.balanceLeft),    color: ci.balanceLeft > 0 ? '#dc2626' : '#16a34a' },
        ].map(s => (
          <div key={s.label} style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 600, marginBottom: 3 }}>{s.label}</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 10, display: 'flex', justifyContent: 'center' }}>
        <span style={{ padding: '3px 12px', borderRadius: 99, fontSize: 11, fontWeight: 700, background: statusBg, color: statusColor, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          {ci.creditStatus === 'settled' ? <FiCheckCircle size={11} /> : ci.isOverdue ? <FiAlertCircle size={11} /> : <FiClock size={11} />}
          {statusLabel}
        </span>
      </div>
    </div>
  );
};

/* ────────────────────────────────────────────────────────────────────
   Payment History Modal
   Reads order.paymentEntries — written back by syncOrderFromReceipts()
   on the backend whenever a ClientReceipt is created or reversed.
   Admins can edit a payment from here (EditReceiptModal).
   ──────────────────────────────────────────────────────────────────── */

const PaymentHistoryModal = ({ order, onClose, onChanged }) => {
  const entries = Array.isArray(order.paymentEntries) ? order.paymentEntries : [];
  const { isAdmin } = useAuth();
  const [receipts, setReceipts]   = useState([]);
  const [editingId, setEditingId] = useState(null);

  const loadReceipts = useCallback(() => {
    API.get(`/client-receipts/by-order/${order.id}`)
      .then(({ data }) => setReceipts(data.receipts || []))
      .catch(() => {});
  }, [order.id]);
  useEffect(() => { loadReceipts(); }, [loadReceipts]);

  // receipts keyed by number so a payment row can offer "Edit"
  const byNumber = new Map(receipts.map(r => [r.receiptNumber, r]));
  const entryNumbers = new Set(entries.map(e => e.receiptNumber).filter(Boolean));
  // active receipts that paid this order through a bill (not shown as their own row above)
  const billReceipts = receipts.filter(r => r.status === 'active' && !entryNumbers.has(r.receiptNumber));
  const canEditRow = (r) => isAdmin && r && r.status === 'active';

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: 640, maxWidth: '95vw', maxHeight: '85vh', boxShadow: '0 8px 40px rgba(0,0,0,0.18)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>Payment History</h3>
            <div style={{ fontSize: 13, color: '#6b7280', marginTop: 3 }}>
              {order.orderNumber} · {order.companyName || order.customerName}
            </div>
          </div>
          <button onClick={onClose} style={{ background: '#f3f4f6', border: 'none', borderRadius: 8, padding: '6px 8px', cursor: 'pointer' }}>
            <FiX size={16} />
          </button>
        </div>

        {order.paymentType === 'credit' && <CreditBlock order={order} />}

        <div style={{ overflowY: 'auto', flex: 1 }}>
          {entries.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px 0', color: '#9ca3af', fontSize: 14 }}>
              No payments received yet.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                  {['Receipt No', 'Date', 'Mode', 'Account', 'Amount', 'Note', ''].map(h => (
                    <th key={h} style={{ padding: '9px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {entries.map((e, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '10px 12px', fontWeight: 700, color: '#6366f1' }}>{e.receiptNumber || '—'}</td>
                    <td style={{ padding: '10px 12px', color: '#374151' }}>{fmt(e.date)}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: '#eff6ff', color: '#2563eb' }}>
                        {e.mode || 'cash'}
                      </span>
                    </td>
                    <td style={{ padding: '10px 12px', color: '#374151' }}>{e.bank_account_label || '—'}</td>
                    <td style={{ padding: '10px 12px', fontWeight: 700, color: '#16a34a' }}>{money(e.amount)}</td>
                    <td style={{ padding: '10px 12px', color: '#6b7280' }}>{e.note || '—'}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                      {canEditRow(byNumber.get(e.receiptNumber)) && (
                        <button onClick={() => setEditingId(byNumber.get(e.receiptNumber).id)}
                          style={{ padding: '4px 9px', background: '#eef2ff', color: '#4f46e5', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <FiEdit2 size={11} /> Edit
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {billReceipts.length > 0 && (
            <div style={{ marginTop: 18 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
                Paid through bill receipts
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <tbody>
                  {billReceipts.map(r => (
                    <tr key={r.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '9px 12px', fontWeight: 700, color: '#6366f1' }}>{r.receiptNumber}</td>
                      <td style={{ padding: '9px 12px', color: '#374151' }}>{fmt(r.receiptDate)}</td>
                      <td style={{ padding: '9px 12px', color: '#6b7280' }}>
                        {(r.billAllocations || []).map(a => `${a.billNumber}: ${money(Number(a.allocatedAmount) + Number(a.tdsAmount || 0))}`).join(', ')}
                      </td>
                      <td style={{ padding: '9px 12px', fontWeight: 700, color: '#16a34a' }}>
                        {money(Number(r.totalAmount) + Number(r.creditUsed) + Number(r.tdsAmount || 0))}
                        {Number(r.tdsAmount) > 0 && (
                          <div style={{ fontSize: 11, fontWeight: 600, color: '#b45309' }}>incl. TDS {money(r.tdsAmount)}</div>
                        )}
                      </td>
                      <td style={{ padding: '9px 12px', textAlign: 'right' }}>
                        {r.editCount > 0 && <span style={{ marginRight: 6, fontSize: 11, color: '#6b7280' }}>Edited</span>}
                        {canEditRow(r) && (
                          <button onClick={() => setEditingId(r.id)}
                            style={{ padding: '4px 9px', background: '#eef2ff', color: '#4f46e5', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <FiEdit2 size={11} /> Edit
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {editingId && (
          <EditReceiptModal
            receiptId={editingId}
            onClose={() => setEditingId(null)}
            onSaved={() => { loadReceipts(); onChanged && onChanged(); }}
          />
        )}

        {order.paymentType !== 'credit' && (
          <div style={{ marginTop: 14, padding: '10px 14px', background: '#f9fafb', borderRadius: 8, display: 'flex', gap: 20, fontSize: 13 }}>
            <span>Grand Total: <strong>{money(order.grandTotal)}</strong></span>
            <span style={{ color: '#16a34a' }}>Received: <strong>{money(order.amountReceived)}</strong></span>
            <span style={{ color: '#dc2626' }}>Balance: <strong>{money(Math.max(0, order.grandTotal - order.amountReceived))}</strong></span>
          </div>
        )}

        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '9px 22px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#f9fafb', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

/* ────────────────────────────────────────────────────────────────────
   Record Payment Modal
   Replaces the old LogPaymentModal.
   Now creates a ClientReceipt (salesOrderId-linked) instead of writing
   directly to SalesOrder.paymentEntries — so the ledger sees it.
   ──────────────────────────────────────────────────────────────────── */

const RecordPaymentModal = ({ order, onClose, onSaved }) => {
  const { log } = useStaffLog();
  const [form, setForm]               = useState({
    amount: '', date: new Date().toISOString().slice(0, 10),
    mode: 'cash', bankAccountId: '', note: '',
  });
  const [saving, setSaving]           = useState(false);
  const [liveOrder, setLiveOrder]     = useState(order);
  const [loadingLive, setLoadingLive] = useState(true);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [nextNumber, setNextNumber]   = useState('');

  const set = (field, value) => setForm(f => ({ ...f, [field]: value }));

  // Load bank accounts
  useEffect(() => {
    API.get('/company-bank-accounts', { params: { active: 'true' } })
      .then(({ data }) => setBankAccounts(data.accounts || []))
      .catch(() => {});
  }, []);

  // Fetch live order balance
  useEffect(() => {
    let cancelled = false;
    API.get(`/sales-orders/${order.id}`)
      .then(({ data }) => { if (!cancelled && data.order) setLiveOrder(data.order); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoadingLive(false); });
    return () => { cancelled = true; };
  }, [order.id]);

  // Auto-fetch next receipt number
  useEffect(() => {
    API.get('/client-receipts/next-number', { params: { type: 'order' } })
      .then(({ data }) => setNextNumber(data.receiptNumber || ''))
      .catch(() => {});
  }, []);

  const ci         = getCreditInfo(liveOrder);
  const grandTotal = Number(liveOrder.grandTotal) || 0;
  const maxAmount  = ci
    ? ci.balanceLeft
    : Math.max(0, grandTotal - Number(liveOrder.amountReceived || 0));

  const handleSave = async () => {
    const amt = Number(form.amount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    if (amt > maxAmount + 0.01) {
      toast.error(`Amount exceeds balance (max ${money(maxAmount)})`);
      return;
    }

    try {
      setSaving(true);

      const payload = {
        salesOrderId:     liveOrder.id,
        businessId:       liveOrder.businessId || null,
        customerName:     liveOrder.customerName || '',
        customerPhone:    liveOrder.customerPhone || '',
        companyName:      liveOrder.companyName || '',
        totalAmount:      amt,
        creditUsed:       0,
        receiptDate:      form.date,
        mode:             form.mode,
        bankAccountId:    form.bankAccountId || null,
        bankAccountLabel: bankAccounts.find(a => String(a.id) === String(form.bankAccountId))?.account_name || null,
        note:             form.note.trim(),
        billAllocations:  [], // order-linked — no bill allocations
      };

      await API.post('/client-receipts', payload);

      // Reload the order so table reflects updated amountReceived
      const { data: refreshed } = await API.get(`/sales-orders/${liveOrder.id}`);
      toast.success(`Payment recorded — Receipt ${nextNumber}`);
      await log('recorded_payment', `Recorded payment Receipt ${nextNumber} for order #${liveOrder.orderNumber}`, 'SalesOrder', liveOrder.id);
      onSaved(refreshed.order || liveOrder);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save payment');
    } finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 14, padding: 28, width: 580, maxWidth: '95vw', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>

        <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 700 }}>Record Payment</h3>
        <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 8 }}>
          {liveOrder.orderNumber} · {liveOrder.companyName || liveOrder.customerName}
          {loadingLive && <span style={{ marginLeft: 8, color: '#9ca3af' }}>(refreshing…)</span>}
        </div>

        {/* Auto receipt number badge */}
        {nextNumber && (
          <div style={{ marginBottom: 14, display: 'inline-flex', alignItems: 'center', gap: 8, padding: '5px 12px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 7, fontSize: 13 }}>
            Receipt No: <strong style={{ color: '#16a34a' }}>{nextNumber}</strong>
            <span style={{ fontSize: 11, color: '#6b7280' }}>(auto-assigned)</span>
          </div>
        )}

        {/* Credit block or cash balance */}
        {liveOrder.paymentType === 'credit' && <CreditBlock order={liveOrder} />}
        {liveOrder.paymentType !== 'credit' && (
          <div style={{ display: 'flex', gap: 12, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '8px 12px', marginBottom: 16, fontSize: 13 }}>
            <span style={{ color: '#15803d' }}>Received: <strong>{money(liveOrder.amountReceived)}</strong></span>
            <span style={{ color: '#dc2626' }}>Balance: <strong>{money(maxAmount)}</strong></span>
            <span style={{ color: '#111' }}>Total: <strong>{money(grandTotal)}</strong></span>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div>
            <label style={labelStyle}>Amount (₹) *</label>
            <input
              type="number" min="0.01" max={maxAmount} step="0.01"
              value={form.amount}
              onChange={e => set('amount', e.target.value)}
              placeholder={`Max ${money(maxAmount)}`}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Date</label>
            <input
              type="date" value={form.date}
              onChange={e => set('date', e.target.value)}
              onClick={e => e.currentTarget.showPicker?.()}
              style={{ ...inputStyle, cursor: 'pointer' }}
            />
          </div>
          <div>
            <label style={labelStyle}>Mode</label>
            <select value={form.mode} onChange={e => { set('mode', e.target.value); set('bankAccountId', ''); }} style={inputStyle}>
              <option value="cash">Cash</option>
              <option value="upi">UPI</option>
              <option value="cheque">Cheque</option>
              <option value="bank_transfer">Bank Transfer</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Note</label>
            <input type="text" value={form.note} onChange={e => set('note', e.target.value)} placeholder="Optional" style={inputStyle} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Received In Account</label>
            <select value={form.bankAccountId} onChange={e => set('bankAccountId', e.target.value)} style={inputStyle}>
              <option value="">— Select Account —</option>
              {bankAccounts
                .filter(a => {
                  if (form.mode === 'cash')                             return a.account_type === 'cash';
                  if (form.mode === 'upi')                              return a.account_type === 'upi';
                  if (form.mode === 'bank_transfer' || form.mode === 'cheque') return a.account_type === 'bank';
                  return true;
                })
                .map(a => (
                  <option key={a.id} value={a.id}>
                    {a.account_name}{a.account_number_last4 ? ` (••••${a.account_number_last4})` : ''}
                  </option>
                ))
              }
            </select>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
          <button onClick={onClose} style={{ padding: '9px 20px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#f9fafb', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving} style={{ padding: '9px 20px', borderRadius: 8, border: 'none', background: 'var(--primary)', color: '#fff', fontWeight: 600, fontSize: 13, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Saving…' : 'Save Payment'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ────────────────────────────────────────────────────────────────────
   Main AdminPayments Page
   ──────────────────────────────────────────────────────────────────── */

const PAGE_SIZES   = [25, 50, 100];
const EXPORT_LIMIT = 10000; // export pulls ALL matching orders, not just the visible page

// 'YYYY-MM-DD' → '01 Aug 2026' (no timezone shift)
const fmtYMD = (ymd) => {
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

// Builds the query string from the filter state — shared by table load and exports
const buildParams = (f, targetPage, limit) => {
  const params = new URLSearchParams();
  if (f.search.trim()) params.set('q', f.search.trim());
  params.set('page', targetPage);
  params.set('limit', limit);
  params.set('withTotals', 'true');
  if (f.filterStatus !== 'all') params.set('paymentStatus', f.filterStatus);
  if (f.dateFrom) params.set('orderFrom', f.dateFrom);
  if (f.dateTo)   params.set('orderTo', f.dateTo);
  const c = f.customers.find(x => x.key === f.customerKey);
  if (c) {
    if (c.businessId) params.set('businessId', c.businessId);
    else {
      params.set('customerName', c.customerName);
      params.set('customerPhone', c.customerPhone);
    }
  }
  return params;
};

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// CSV cell: quote when needed, and neutralise spreadsheet formulas (=, +, -, @)
const csvCell = (v, isText = false) => {
  let s = String(v ?? '');
  if (isText && /^[=+\-@]/.test(s)) s = `'${s}`;
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/* Footer bar row: label on the left, three amounts on the right */
const TotalsRow = ({ title, sub, grand, received, balance, strong }) => (
  <div style={{
    display: 'grid', gridTemplateColumns: 'minmax(170px, 1fr) repeat(3, minmax(110px, auto))',
    gap: 16, alignItems: 'center', padding: '6px 0',
  }}>
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 13, fontWeight: strong ? 800 : 700, color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={title}>{title}</div>
      <div className="so-muted">{sub}</div>
    </div>
    {[
      ['Grand Total', grand,    '#111'],
      ['Received',    received, '#16a34a'],
      ['Balance',     balance,  '#dc2626'],
    ].map(([label, val, color]) => (
      <div key={label} style={{ textAlign: 'right' }}>
        <div style={{ fontSize: 10, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
        <div style={{ fontSize: strong ? 15 : 14, fontWeight: strong ? 800 : 700, color }}>{money(val)}</div>
      </div>
    ))}
  </div>
);

const AdminPayments = () => {
  const [orders, setOrders]             = useState([]);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [dateFrom, setDateFrom]         = useState('');
  const [dateTo, setDateTo]             = useState('');
  const [customerKey, setCustomerKey]   = useState('');
  const [customers, setCustomers]       = useState([]);
  const [pageSize, setPageSize]         = useState(PAGE_SIZES[0]);
  const [page, setPage]                 = useState(1);
  const [totalPages, setTotalPages]     = useState(1);
  const [totalCount, setTotalCount]     = useState(0);
  const [totals, setTotals]             = useState(null); // totals for ALL matching orders (from backend)
  const [exporting, setExporting]       = useState(null); // 'csv' | 'pdf' | null
  const [logModal, setLogModal]         = useState(null);
  const [historyModal, setHistoryModal] = useState(null);
  const navigate = useNavigate();

  const reqIdRef = useRef(0);     // latest-request-wins guard
  const firstSearchRun = useRef(true);

  // Always-current filter values, so debounced/async callbacks never use stale state
  const fRef = useRef({});
  fRef.current = { search, filterStatus, dateFrom, dateTo, customerKey, customers, pageSize };

  const load = useCallback(async ({ targetPage = 1, silent = false } = {}) => {
    const f = fRef.current;
    const reqId = ++reqIdRef.current;
    if (!silent) setLoading(true);
    try {
      const { data } = await API.get(`/sales-orders?${buildParams(f, targetPage, f.pageSize)}`);
      if (reqId !== reqIdRef.current) return; // a newer request has started — ignore this response
      setOrders(data.orders || []);
      setTotalPages(data.pages || 1);
      setTotalCount(data.count || 0);
      setTotals(data.totals || null);
      setPage(targetPage);
    } catch {
      if (reqId === reqIdRef.current) toast.error('Could not load orders');
    } finally {
      if (reqId === reqIdRef.current) setLoading(false);
    }
  }, []);

  // Customer dropdown list (once)
  useEffect(() => {
    API.get('/sales-orders/customers')
      .then(({ data }) => setCustomers(data.customers || []))
      .catch(() => {});
  }, []);

  // Status / dates / customer / page size — reload immediately, back to page 1
  useEffect(() => {
    load({ targetPage: 1 });
  }, [filterStatus, dateFrom, dateTo, customerKey, pageSize, load]);

  // Search — debounced, back to page 1 (skip first run: the effect above already loads)
  useEffect(() => {
    if (firstSearchRun.current) { firstSearchRun.current = false; return; }
    const t = setTimeout(() => load({ targetPage: 1 }), 350);
    return () => clearTimeout(t);
  }, [search, load]);

  const updateOrder = (updated) => {
    setOrders(prev => prev.map(o => o.id === updated.id ? updated : o));
    setHistoryModal(prev => prev && prev.id === updated.id ? updated : prev);
  };

  // orders already filtered by backend; exclude any cancelled that slipped through
  const filtered = orders.filter(o => o.status !== 'cancelled');

  // ── Footer totals ──
  const pageTotals = filtered.reduce((acc, o) => {
    const a = getAmounts(o);
    acc.grand += a.grand; acc.received += a.received; acc.balance += a.balance;
    return acc;
  }, { grand: 0, received: 0, balance: 0 });

  // eslint-disable-next-line no-unused-vars
  const allTotals = totals
    ? { grand: totals.grandTotal, received: totals.received, balance: totals.balance }
    : pageTotals;
  // eslint-disable-next-line no-unused-vars
  const allCount = totals ? totals.orderCount : totalCount;

  const selectedCustomer = customers.find(c => c.key === customerKey);
  const hasFilter = filterStatus !== 'all' || dateFrom || dateTo || customerKey || search.trim() !== '';
  const singlePage = totalPages <= 1;

  const filterLabel = hasFilter
    ? [
        filterStatus !== 'all' ? `Filter: ${STATUS_LABELS[filterStatus] || filterStatus}` : null,
        selectedCustomer ? `Customer: ${selectedCustomer.label}` : null,
        (dateFrom || dateTo)
          ? `Date: ${dateFrom ? fmtYMD(dateFrom) : 'start'} – ${dateTo ? fmtYMD(dateTo) : 'today'}`
          : null,
        search.trim() ? `Search: "${search.trim()}"` : null,
      ].filter(Boolean).join(' · ')
    : 'All orders';

  const clearAll = () => {
    setFilterStatus('all'); setDateFrom(''); setDateTo('');
    setCustomerKey(''); setSearch('');
  };

  /* ── Export (always ALL matching orders, not only the visible page) ── */
  const fetchForExport = async () => {
    const f = fRef.current;
    const { data } = await API.get(`/sales-orders?${buildParams(f, 1, EXPORT_LIMIT)}`);
    const list = (data.orders || []).filter(o => o.status !== 'cancelled');
    const rows = list.map(o => {
      const a  = getAmounts(o);
      const ps = getPaymentStatus(o);
      return {
        orderNumber: o.orderNumber || '',
        date:        fmt(o.orderDate),
        company:     o.companyName || '',
        customer:    o.customerName || '',
        phone:       o.customerPhone || '',
        paymentType: o.paymentType || '',
        dueDate:     o.paymentType === 'credit' && o.dueDate ? fmt(o.dueDate) : '',
        grand: a.grand, received: a.received, balance: a.balance,
        status: (STATUS_META[ps] || STATUS_META.unpaid).label,
      };
    });
    const t = data.totals
      ? { orderCount: data.totals.orderCount, grand: data.totals.grandTotal, received: data.totals.received, balance: data.totals.balance }
      : {
          orderCount: rows.length,
          grand:    rows.reduce((s, r) => s + r.grand, 0),
          received: rows.reduce((s, r) => s + r.received, 0),
          balance:  rows.reduce((s, r) => s + r.balance, 0),
        };
    return { rows, totals: t };
  };

  const exportCSV = async () => {
    setExporting('csv');
    try {
      const { rows, totals: t } = await fetchForExport();
      if (!rows.length) { toast.error('Nothing to export'); return; }
      const header = ['Order No', 'Order Date', 'Company', 'Customer', 'Phone', 'Payment Type', 'Due Date', 'Grand Total', 'Received', 'Balance', 'Status'];
      const lines = [header.map(h => csvCell(h)).join(',')];
      rows.forEach(r => lines.push([
        csvCell(r.orderNumber, true), csvCell(r.date), csvCell(r.company, true), csvCell(r.customer, true),
        csvCell(r.phone, true), csvCell(r.paymentType), csvCell(r.dueDate),
        r.grand.toFixed(2), r.received.toFixed(2), r.balance.toFixed(2), csvCell(r.status),
      ].join(',')));
      lines.push(['', '', '', '', '', '', csvCell(`TOTAL (${t.orderCount} orders)`), t.grand.toFixed(2), t.received.toFixed(2), t.balance.toFixed(2), ''].join(','));
      // BOM so Excel reads UTF-8 names correctly
      downloadBlob(new Blob(['\uFEFF' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' }),
        `payments_${new Date().toISOString().slice(0, 10)}.csv`);
      toast.success(`Exported ${rows.length} orders`);
    } catch { toast.error('Export failed'); }
    finally { setExporting(null); }
  };

  const exportPDF = async () => {
    setExporting('pdf');
    try {
      const { rows, totals: t } = await fetchForExport();
      if (!rows.length) { toast.error('Nothing to export'); return; }
      const bytes = await generatePaymentsPDF({
        rows, totals: t, filterLabel,
        generatedOn: new Date().toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }),
      });
      downloadBlob(new Blob([bytes], { type: 'application/pdf' }),
        `payments_${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success(`Exported ${rows.length} orders`);
    } catch { toast.error('PDF export failed'); }
    finally { setExporting(null); }
  };

  const exportBtnStyle = (busy) => ({ opacity: busy ? 0.6 : 1, cursor: busy ? 'wait' : 'pointer' });

  return (
    <div className="so-page">

      <div className="so-page-head">
        <div><h2>Receipts</h2></div>
        <div className="so-page-head-actions">
          <button className="so-btn" onClick={exportCSV} disabled={!!exporting} style={exportBtnStyle(exporting === 'csv')} title="Download all matching orders as CSV">
            <FiDownload size={14} /> {exporting === 'csv' ? 'Exporting…' : 'CSV'}
          </button>
          <button className="so-btn" onClick={exportPDF} disabled={!!exporting} style={exportBtnStyle(exporting === 'pdf')} title="Download all matching orders as PDF">
            <FiFileText size={14} /> {exporting === 'pdf' ? 'Exporting…' : 'PDF'}
          </button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="so-filter-bar">
        <div className="so-search-input so-search-input--filter">
          <FiSearch size={15} />
          <input
            placeholder="Search order, customer, phone…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        <select
          className="so-date-input"
          style={{ width: 190 }}
          value={customerKey}
          onChange={e => setCustomerKey(e.target.value)}
          title="Filter by customer"
        >
          <option value="">All customers</option>
          {customers.map(c => (
            <option key={c.key} value={c.key}>
              {c.label}{c.companyName && c.customerName ? ` (${c.customerName})` : ''}
            </option>
          ))}
        </select>

        <div className="so-filter-dates">
          <span className="so-date-label">Order date</span>
          <input
            type="date" className="so-date-input" value={dateFrom}
            max={dateTo || undefined}
            onChange={e => setDateFrom(e.target.value)}
            onClick={e => e.currentTarget.showPicker?.()}
            title="From date"
          />
          <span className="so-date-sep">–</span>
          <input
            type="date" className="so-date-input" value={dateTo}
            min={dateFrom || undefined}
            onChange={e => setDateTo(e.target.value)}
            onClick={e => e.currentTarget.showPicker?.()}
            title="To date"
          />
        </div>

        <div className="so-pill-group">
          {[
            ['all',    'All'],
            ['action', 'Needs Action'],
            ['credit', 'Credit'],
            ['overdue','Overdue'],
            ['paid',   'Paid'],
          ].map(([val, label]) => (
            <button
              key={val}
              className={`so-pill${filterStatus === val ? ' so-pill--active' : ''}`}
              onClick={() => setFilterStatus(val)}
            >{label}</button>
          ))}
        </div>

        <div className="so-filter-dates">
          <span className="so-date-label">Show</span>
          <select
            className="so-date-input" style={{ width: 70 }}
            value={pageSize}
            onChange={e => setPageSize(Number(e.target.value))}
            title="Orders per page"
          >
            {PAGE_SIZES.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>

        {hasFilter && (
          <button className="so-clear-btn" onClick={clearAll}>Clear</button>
        )}
      </div>

      {/* Table */}
      <div className="so-card so-card--table">
        {loading ? (
          <div className="so-empty">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="so-empty">
            {hasFilter ? 'No matching orders.' : 'No orders yet.'}
          </div>
        ) : (
          <>
          <div className="so-table-scroll">
            <table className="so-table">
              <thead>
                <tr>
                  <th>Order #</th>
                  <th>Customer</th>
                  <th>Grand Total</th>
                  <th>Received</th>
                  <th>Balance / Credit</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(o => {
                  const pStatus = getPaymentStatus(o);
                  const sm      = STATUS_META[pStatus] || STATUS_META.unpaid;
                  const ci      = getCreditInfo(o);
                  const { grand, received, balance } = getAmounts(o);
                  const entries  = Array.isArray(o.paymentEntries) ? o.paymentEntries : [];

                  return (
                    <tr key={o.id}>
                      <td>
                        <div className="so-strong">{o.orderNumber}</div>
                        <div className="so-muted">{fmt(o.orderDate)}</div>
                      </td>
                      <td>
                        <div className="so-strong">{o.companyName || o.customerName}</div>
                        <div className="so-muted">{o.companyName ? o.customerName : o.customerPhone}</div>
                      </td>
                      <td className="so-strong">{money(grand)}</td>
                      <td style={{ color: '#16a34a', fontWeight: 600 }}>{money(received)}</td>
                      <td>
                        {balance > 0 ? (
                          <>
                            <span style={{ color: ci ? '#7c3aed' : '#dc2626', fontWeight: 600 }}>
                              {money(balance)}
                            </span>
                            {ci && o.dueDate && (
                              <div className="so-muted" style={{ color: ci.isOverdue ? '#b91c1c' : '#6b7280' }}>
                                Due: {fmt(o.dueDate)}{ci.isOverdue ? ' ⚠️' : ''}
                              </div>
                            )}
                          </>
                        ) : (
                          <span style={{ color: '#6b7280' }}>—</span>
                        )}
                      </td>
                      <td>
                        <span className="so-badge" style={{ background: sm.bg, color: sm.color }}>
                          {sm.label}
                        </span>
                      </td>
                      <td>
                        {o.billingStatus === 'billed' && o.billId ? (
                          /* Fully billed order — go to bill for payment */
                          <button
                            onClick={() => navigate(`/admin/billing/${o.billId}`)}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 4,
                              padding: '4px 10px', borderRadius: 6,
                              border: '1px solid #0284c7', background: '#0284c7',
                              color: '#fff', fontSize: 11, fontWeight: 600, cursor: 'pointer',
                            }}
                          >
                            <FiExternalLink size={11} /> View Bill
                          </button>
                        ) : (
                          /* Unbilled OR partially billed order — record payment via ClientReceipt.
                             Partially billed: payments for the billed part go on the bill,
                             advance for the unbilled part is recorded here on the order. */
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                            {o.billingStatus === 'partial' && o.billId && (
                              <button
                                onClick={() => navigate(`/admin/billing/${o.billId}`)}
                                title="Part of this order is billed — open the latest bill"
                                style={{
                                  display: 'inline-flex', alignItems: 'center', gap: 4,
                                  padding: '4px 10px', borderRadius: 6,
                                  border: '1px solid #d97706', background: '#fef3c7',
                                  color: '#92400e', fontSize: 11, fontWeight: 600, cursor: 'pointer',
                                }}
                              >
                                <FiExternalLink size={11} /> Partial · View Bill
                              </button>
                            )}
                            {pStatus !== 'paid' && (
                              <button
                                onClick={() => setLogModal(o)}
                                title="Record Payment"
                                style={{
                                  display: 'flex', alignItems: 'center', gap: 4,
                                  padding: '5px 10px', borderRadius: 7,
                                  border: '1px solid var(--primary)', background: 'var(--primary)',
                                  color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                                }}
                              >
                                <FiPlus size={12} /> Record
                              </button>
                            )}
                            {entries.length > 0 && (
                              <button
                                onClick={() => setHistoryModal(o)}
                                title="Payment History"
                                style={{
                                  display: 'flex', alignItems: 'center', gap: 4,
                                  padding: '5px 10px', borderRadius: 7,
                                  border: '1px solid #6b7280', background: '#f3f4f6',
                                  color: '#374151', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                                }}
                              >
                                <FiList size={12} /> {entries.length}
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
            {/* Totals — attached to the bottom of the table */}
            <div style={{
              background: '#f5f7ff', borderTop: '2px solid #c7d2fe', padding: '6px 18px', overflowX: 'auto',
            }}>
              <TotalsRow
                title={singlePage ? 'Total' : `Page ${page} total`}
                sub={`${filtered.length} orders`}
                grand={pageTotals.grand} received={pageTotals.received} balance={pageTotals.balance}
              />
              {/* GRAND TOTAL (ALL PAGES) ROW — hidden for now, uncomment to show again
              <div style={{ borderTop: '1px solid #e0e7ff' }} />
              <TotalsRow
                strong
                title={`${singlePage ? 'Total' : 'Grand total'} — ${filterLabel}`}
                sub={`${allCount} orders`}
                grand={allTotals.grand} received={allTotals.received} balance={allTotals.balance}
              />
              */}
            </div>
          </>
        )}
      </div>

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, flexWrap: 'wrap', gap: 8 }}>
          <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
            Page {page} of {totalPages} &nbsp;·&nbsp; {totalCount} orders
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="so-btn" onClick={() => load({ targetPage: page - 1 })} disabled={page <= 1} style={{ opacity: page <= 1 ? 0.4 : 1 }}>‹ Prev</button>
            <button className="so-btn" onClick={() => load({ targetPage: page + 1 })} disabled={page >= totalPages} style={{ opacity: page >= totalPages ? 0.4 : 1 }}>Next ›</button>
          </div>
        </div>
      )}

      {/* Modals */}
      {logModal && (
        <RecordPaymentModal
          order={logModal}
          onClose={() => setLogModal(null)}
          onSaved={(updated) => { updateOrder(updated); setLogModal(null); load({ targetPage: page, silent: true }); }}
        />
      )}
      {historyModal && (
        <PaymentHistoryModal
          order={historyModal}
          onClose={() => setHistoryModal(null)}
          onChanged={async () => {
            try {
              const { data } = await API.get(`/sales-orders/${historyModal.id}`);
              if (data.order) updateOrder(data.order);
            } catch { /* list refresh below still runs */ }
            load({ targetPage: page, silent: true });
          }}
        />
      )}
    </div>
  );
};

export default AdminPayments;